import { createFileRoute } from "@tanstack/react-router";
import { byCategory } from "@/data/products";
import { site, pageUrl } from "@/data/site";
import { Breadcrumbs, PageHeader, Section, SectionHeading } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { ProductGrid } from "@/components/site/product-grid";
import { FinalCTA } from "@/components/site/final-cta";

export const Route = createFileRoute("/for-pen-turners")({
  head: () => ({
    meta: [
      { title: "For Pen Turners | Blanks & Components | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "Fordite, colorwood, .30 and .50 caliber components, tube-on clear-cast blanks, embedded object blanks and bottle stopper blanks for pen turners.",
      },
      { property: "og:title", content: "Blanks and components for pen turners" },
      {
        property: "og:description",
        content: "Fordite, cartridge components, clear-cast and embedded object blanks from one turner to another.",
      },
      { property: "og:url", content: pageUrl("/for-pen-turners") },
    ],
    links: [{ rel: "canonical", href: pageUrl("/for-pen-turners") }],
  }),
  component: ForPenTurners,
});

const resources = [
  {
    title: "Downloads",
    slug: "downloads",
    body: "Reference sheets and templates Paul keeps for his own builds. Ask Paul which files are currently available — the download library is being rebuilt as part of this site.",
    state: "Available on request",
  },
  {
    title: "Fordite and colorwood",
    slug: "fordite-and-colorwood",
    body: "Layered automotive paint slag and laminated colorwood, cut and stabilized for turning. Cut slowly at low speed.",
    state: "Ask about current runs",
  },
  {
    title: ".30 and .50 caliber components",
    slug: "cal-components",
    body: "Cartridge cases and matching hardware for cartridge pen builds.",
    state: "Ask about current stock",
  },
  {
    title: "Tube-on clear-cast blanks",
    slug: "tube-on-clear-cast-blanks",
    body: "Cast directly onto the tube, so no drilling and nothing shifts during preparation.",
    state: "Ask about current stock",
  },
  {
    title: "Embedded object blanks",
    slug: "embedded-object-blanks",
    body: "Send Paul the object and he will advise whether it will survive casting and turning.",
    state: "Made to order",
  },
  {
    title: "Bottle stopper blanks",
    slug: "bottle-stopper-blanks",
    body: "Porcelain roses and fishing flies cast over a color base, sized for a standard stopper mandrel.",
    state: "Ask about current stock",
  },
];

function ForPenTurners() {
  const items = byCategory("pen-turners");
  return (
    <>
      <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "For Pen Turners" }]} />
      <PageHeader
        eyebrow="For pen turners"
        title="Material and components, turner to turner."
        lede="This section is for people who already have a lathe. If you are here for a gift, the Pens and Pencils page is the better starting point."
      >
        <CtaLink to="/contact" variant="outline" className="h-12">
          Ask a technical question
        </CtaLink>
        <CtaLink to="/custom-pens" variant="quiet" className="h-12">
          Looking for a finished pen instead?
        </CtaLink>
      </PageHeader>

      <Section tone="paper" labelledBy="pt-resources-heading">
        <div className="shell">
          <SectionHeading
            id="pt-resources-heading"
            eyebrow="Categories"
            title="What Paul keeps on the shelf."
            lede="Stock moves with what Paul can source, so availability is confirmed per order rather than listed as a live count."
          />
          <ul className="mt-12 grid gap-px border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-3">
            {resources.map((r) => (
              <li key={r.title} id={r.slug} className="scroll-mt-28 bg-background p-6">
                <h3 className="font-[family-name:var(--font-display)] text-xl">{r.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{r.body}</p>
                <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-brass/50 px-3 py-1 text-[0.66rem] font-semibold uppercase tracking-[0.1em] text-walnut">
                  <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
                  {r.state}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section labelledBy="pt-items-heading">
        <div className="shell">
          <SectionHeading
            id="pt-items-heading"
            eyebrow="Listings"
            title="Blanks and components."
            lede="Pricing depends on size, material and quantity. Tell Paul what you are building and how many you need."
          />
          <ProductGrid products={items} showRequestSimilar={false} />
        </div>
      </Section>

      <Section tone="paper" labelledBy="pt-contact-heading">
        <div className="shell grid gap-10 lg:grid-cols-2 lg:items-center">
          <SectionHeading
            id="pt-contact-heading"
            eyebrow="Specialized questions"
            title="Talk to another turner."
            lede="Speeds, finishes, casting failures, tube slippage, stabilizing burl — Paul is happy to talk shop before you order anything."
          />
          <div className="surface-card p-8">
            <p className="text-sm text-muted-foreground">Direct line to the shop:</p>
            <a
              href={`mailto:${site.email}`}
              className="mt-2 block font-[family-name:var(--font-display)] text-2xl text-ink underline underline-offset-4"
            >
              {site.email}
            </a>
            <a href={`tel:${site.phoneHref}`} className="mt-3 block text-lg text-walnut underline underline-offset-4">
              {site.phoneDisplay}
            </a>
          </div>
        </div>
      </Section>

      <FinalCTA
        heading="Need something cast, cut or sourced?"
        body="Tell Paul the dimensions, the material and the quantity, and he will confirm what he can supply."
        label="Send Paul the specification"
      />
    </>
  );
}
